/**
 * @module AdapterList
 * @package @geenius/adapters/react-ant
 * @description Renders the searchable tokenized Ant Design adapter grid for the React variant. The
 * list consumes provider status from context and delegates selection events to
 * the hosting page or dashboard shell.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import theme from "antd/lib/theme/index.js";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useAdapters } from "../hooks";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for rendering the React adapter list and optional status filter controls.
 *
 * @property onSelect Optional callback invoked when a domain card is selected.
 * @property filterStatus Optional status filter applied before rendering the grid.
 * @property className Optional class name merged into the outer wrapper.
 * @property style Optional inline styles merged into the outer wrapper.
 */
export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  filterStatus?: AdapterStatusType;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders the searchable React adapter list backed by shared provider state.
 *
 * @param props List props including selection callback, status filtering, and wrapper class names.
 * @returns The adapter grid or an empty/loading state when no entries are available.
 */
export function AdapterList({
  onSelect,
  filterStatus,
  className,
  style,
}: AdapterListProps): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [search, setSearch] = useState("");
  const { token } = theme.useToken();

  const filteredDomains = useMemo(() => {
    let domains = [...ADAPTER_DOMAINS];

    if (search) {
      const query = search.toLowerCase();
      domains = domains.filter((domain) => {
        const label = (DOMAIN_LABELS[domain] ?? domain).toLowerCase();
        const provider = statuses[domain]?.provider?.toLowerCase() ?? "";
        return (
          label.includes(query) ||
          provider.includes(query) ||
          domain.includes(query)
        );
      });
    }

    if (filterStatus) {
      domains = domains.filter(
        (domain) => statuses[domain]?.status === filterStatus,
      );
    }

    return domains;
  }, [search, filterStatus, statuses]);

  if (isLoading) {
    return (
      <div
        className={className}
        style={{
          display: "grid",
          gap: token.marginMD,
          ...style,
        }}
        role="status"
        aria-label="Loading adapters"
      >
        {[1, 2, 3, 4].map((index) => (
          <div
            key={index}
            style={{
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorderSecondary}`,
              backgroundColor: token.colorBgContainer,
              padding: token.paddingLG,
              boxShadow: token.boxShadowTertiary,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: token.margin,
              }}
            >
              <div
                style={{
                  height: 48,
                  width: 48,
                  borderRadius: token.borderRadius,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  backgroundColor: token.colorFillAlter,
                }}
              />
              <div
                style={{
                  flex: 1,
                  paddingTop: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: token.marginSM,
                }}
              >
                <div
                  style={{
                    height: 12,
                    width: 96,
                    borderRadius: token.borderRadiusSM,
                    backgroundColor: token.colorFillAlter,
                  }}
                />
                <div
                  style={{
                    height: 8,
                    width: 192,
                    borderRadius: token.borderRadiusSM,
                    backgroundColor: token.colorFillQuaternary,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <div style={{ marginBottom: token.marginMD }}>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search adapters..."
          aria-label="Search adapters"
          style={{
            width: "100%",
            borderRadius: token.borderRadiusSM,
            border: `1px solid ${token.colorBorder}`,
            backgroundColor: token.colorBgContainer,
            padding: "10px 16px",
            fontSize: token.fontSizeSM,
            color: token.colorText,
            outline: "none",
            transition: "all 0.2s",
            boxShadow: token.boxShadowTertiary,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = token.colorPrimary;
            e.currentTarget.style.boxShadow = `0 0 0 2px ${token.colorPrimaryBg}`;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = token.colorBorder;
            e.currentTarget.style.boxShadow = token.boxShadowTertiary;
          }}
        />
      </div>

      {filteredDomains.length === 0 ? (
        <div
          style={{
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorFillQuaternary,
            padding: 32,
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: token.fontSizeSM,
              fontWeight: 500,
              color: token.colorTextSecondary,
              margin: 0,
            }}
          >
            {search
              ? "No adapters match your search"
              : "No adapters configured"}
          </p>
        </div>
      ) : (
        <ul
          style={{
            display: "grid",
            gap: token.marginMD,
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
          aria-label="Adapter list"
        >
          {filteredDomains.map((domain) => (
            <li key={domain}>
              <AdapterCard
                domain={domain}
                status={statuses[domain]}
                onClick={() => onSelect?.(domain)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
